<script setup lang="ts">
import { ref } from 'vue'

withDefaults(
  defineProps<{
    kicker?: string
    title?: string
    meta?: string
    layout?: 'overlay' | 'sidebar'
  }>(),
  {
    kicker: '右侧面板',
    title: '控制中心',
    meta: '',
    layout: 'overlay',
  },
)

const isEmbeddedExample = typeof window !== 'undefined' && window.self !== window.top
// 移动端视口下默认收起侧边面板，避免遮挡渲染画布
const isMobileViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches
const isCollapsed = ref(isMobileViewport)

function reloadEmbeddedExample() {
  window.location.reload()
}

function openEmbeddedExample() {
  window.open(window.location.href, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <aside :class="['control-panel', layout, { 'is-collapsed': isCollapsed }]">
    <button
      v-if="isCollapsed"
      class="panel-expand-button"
      type="button"
      :aria-label="`展开${title}`"
      :aria-expanded="false"
      @click="isCollapsed = false"
    >
      <span>控制中心</span>
      <span aria-hidden="true">☰</span>
    </button>

    <div v-show="!isCollapsed" class="panel-shell">
      <div class="panel-shell-summary">
        <span class="summary-main">
          <span class="panel-kicker">{{ kicker }}</span>
          <span class="panel-title">{{ title }}</span>
        </span>
        <span class="summary-side">
          <span v-if="meta" class="panel-shell-meta">{{ meta }}</span>
          <button
            class="panel-collapse-button"
            type="button"
            :aria-label="`收起${title}`"
            :aria-expanded="true"
            @click="isCollapsed = true"
          >
            ›
          </button>
        </span>
      </div>

      <div class="panel-shell-body">
        <div v-if="isEmbeddedExample" class="panel-embed-actions">
          <button class="panel-embed-action" type="button" @click="reloadEmbeddedExample">
            重新加载
          </button>
          <button class="panel-embed-action" type="button" @click="openEmbeddedExample">
            独立打开
          </button>
        </div>
        <slot />
      </div>
    </div>
  </aside>
</template>

<style>
:has(> .control-panel.sidebar.is-collapsed) {
  grid-template-columns: minmax(0, 1fr) minmax(0, 0fr) !important;
  gap: 0 !important;
}

/* 移动端下侧边面板始终悬浮展示（见下方 .control-panel.sidebar 的 fixed 定位），
   宿主页面的网格不再需要为面板预留列宽，统一收窄为单列铺满画布 */
@media (max-width: 900px) {
  :has(> .control-panel.sidebar) {
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 0 !important;
  }
}
</style>

<style scoped>
.control-panel {
  min-width: 0;
  overflow: hidden;
}

.control-panel.is-collapsed {
  overflow: visible;
}

.control-panel.overlay {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1000;
  width: min(380px, calc(100vw - 32px));
  height: calc(100vh - 32px);
  max-height: calc(100vh - 32px);
  overflow: hidden;
}

.control-panel.sidebar {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: none;
}

.panel-expand-button {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1001;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 13px;
  border: 1px solid rgba(132, 207, 244, 0.16);
  border-radius: 11px;
  background: rgba(9, 16, 24, 0.72);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
  color: rgba(205, 237, 250, 0.78);
  cursor: pointer;
  font-size: 10px;
  backdrop-filter: blur(12px);
}

.panel-expand-button:hover {
  border-color: rgba(132, 207, 244, 0.32);
  background: rgba(15, 29, 41, 0.84);
  color: #d9f5ff;
}

.panel-shell {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(153, 187, 208, 0.3) transparent;
  border-radius: 18px;
  border: 1px solid rgba(160, 196, 220, 0.24);
  background:
    linear-gradient(180deg, rgba(15, 22, 32, 0.92) 0%, rgba(7, 11, 18, 0.88) 100%);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.28);
  backdrop-filter: blur(16px);
  color: #f3f7fb;
}

.panel-shell::-webkit-scrollbar {
  width: 8px;
}

.panel-shell::-webkit-scrollbar-track {
  background: transparent;
}

.panel-shell::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 999px;
  background: rgba(153, 187, 208, 0.26);
  background-clip: padding-box;
}

.panel-shell::-webkit-scrollbar-thumb:hover {
  background: rgba(153, 187, 208, 0.4);
  background-clip: padding-box;
}

.panel-shell-summary {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px;
  background:
    linear-gradient(180deg, rgba(14, 21, 31, 0.96) 0%, rgba(14, 21, 31, 0.92) 72%, rgba(14, 21, 31, 0.8) 100%);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.12);
}

.panel-shell-body {
  min-height: 0;
  padding: 12px 10px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.panel-embed-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.panel-embed-action {
  min-height: 42px;
  padding: 9px 12px;
  border: 1px solid rgba(141, 179, 201, 0.2);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef6fb;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 140ms ease;
}

.panel-embed-action:hover {
  transform: translateY(-1px);
  border-color: rgba(160, 209, 238, 0.42);
  background: rgba(124, 188, 220, 0.12);
}

.summary-main,
.summary-side {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.summary-main {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.summary-side {
  flex-shrink: 0;
}

.panel-collapse-button {
  display: inline-grid;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(136, 210, 247, 0.14);
  border-radius: 9px;
  background: rgba(75, 157, 204, 0.07);
  color: rgba(185, 229, 249, 0.72);
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.panel-collapse-button:hover {
  border-color: rgba(136, 210, 247, 0.3);
  background: rgba(75, 157, 204, 0.14);
  color: #d6f3ff;
}

.panel-kicker {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #82b8d6;
}

.panel-title {
  font-size: 23px;
  font-weight: 700;
  line-height: 1.05;
  color: #f7fbff;
}

.panel-shell-meta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d8e9f4;
}

:deep(.panel-section),
:deep(.panel-subsection) {
  margin: 0;
  overflow: hidden;
  list-style: none;
}

:deep(.panel-section) {
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
}

:deep(.panel-subsection) {
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(0, 0, 0, 0.14);
}

:deep(.panel-section-summary),
:deep(.panel-subsection-summary) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  cursor: pointer;
  list-style: none;
}

:deep(.panel-section-summary) {
  padding: 14px 14px 12px;
}

:deep(.panel-subsection-summary) {
  padding: 12px 12px 10px;
}

:deep(.panel-section-summary::-webkit-details-marker),
:deep(.panel-subsection-summary::-webkit-details-marker) {
  display: none;
}

:deep(.panel-section-body),
:deep(.panel-subsection-body) {
  padding: 0 14px 14px;
}

:deep(.summary-main),
:deep(.summary-side) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

:deep(.summary-main) {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

:deep(.summary-side) {
  flex-shrink: 0;
}

:deep(.summary-chevron) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  font-size: 18px;
  line-height: 1;
  color: #98b8cb;
  transform: rotate(0deg);
  transition: transform 140ms ease;
}

:deep(.panel-section[open] > .panel-section-summary .summary-chevron),
:deep(.panel-subsection[open] > .panel-subsection-summary .summary-chevron) {
  transform: rotate(90deg);
}

:deep(.panel-section-title),
:deep(.panel-subsection-title) {
  font-size: 15px;
  font-weight: 700;
  color: #f4f8fc;
}

:deep(.panel-section-subtitle) {
  font-size: 12px;
  line-height: 1.45;
  color: #9fb7c8;
}

:deep(.summary-count),
:deep(.card-badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

:deep(.summary-count) {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #d8e9f4;
}

:deep(.card-badge) {
  background: rgba(111, 196, 182, 0.16);
  border: 1px solid rgba(111, 196, 182, 0.3);
  color: #c9fff7;
}

:deep(.stacked-panel-body) {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

:deep(.button-grid) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

:deep(.button-grid-two) {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

:deep(.button-grid-three) {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

:deep(.view-actions) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

:deep(.panel-button) {
  appearance: none;
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid rgba(141, 179, 201, 0.2);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  color: #eef6fb;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition:
    border-color 140ms ease,
    background 140ms ease,
    transform 140ms ease,
    box-shadow 140ms ease;
}

:deep(.panel-button:hover:not(:disabled)) {
  transform: translateY(-1px);
  border-color: rgba(160, 209, 238, 0.42);
  background: rgba(124, 188, 220, 0.12);
}

:deep(.panel-button.active) {
  border-color: rgba(67, 220, 196, 0.72);
  background: linear-gradient(180deg, rgba(22, 92, 83, 0.92) 0%, rgba(10, 57, 52, 0.92) 100%);
  box-shadow: inset 0 0 0 1px rgba(125, 255, 232, 0.16);
}

:deep(.panel-button:disabled) {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
}

:deep(.panel-button-wide) {
  width: 100%;
}

:deep(.panel-button-label) {
  display: block;
  font-size: 14px;
  font-weight: 600;
}

:deep(.panel-button-meta) {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: #9eb7c8;
}

:deep(.panel-button-subtle) {
  background: rgba(255, 255, 255, 0.02);
}

:deep(.panel-button-neutral) {
  background: rgba(157, 176, 193, 0.12);
}

:deep(.panel-input) {
  width: 100%;
  min-height: 44px;
  padding: 11px 14px;
  border: 1px solid rgba(152, 191, 214, 0.18);
  border-radius: 14px;
  background: rgba(6, 10, 16, 0.52);
  color: #f3f8fc;
  font-size: 14px;
  outline: none;
}

:deep(.panel-input:focus) {
  border-color: rgba(121, 201, 197, 0.55);
  box-shadow: 0 0 0 3px rgba(121, 201, 197, 0.14);
}

:deep(.panel-field) {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

:deep(.panel-field-label) {
  font-size: 12px;
  font-weight: 600;
  color: #afc7d8;
}

:deep(.panel-note),
:deep(.panel-hint) {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #9fb7c8;
}

:deep(.status-grid) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

:deep(.status-item) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 70px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
}

:deep(.status-label) {
  font-size: 12px;
  color: #92aebf;
}

:deep(.status-value) {
  font-size: 16px;
  font-weight: 700;
  color: #f7fbff;
}

@media (max-width: 900px) {
  .control-panel.overlay,
  .control-panel.sidebar {
    position: fixed;
    width: min(100vw - 20px, 340px);
    top: 10px;
    right: 10px;
    height: calc(100vh - 20px);
    max-height: calc(100vh - 20px);
    z-index: 1000;
  }

  .panel-shell-body {
    padding: 10px;
  }

  .panel-expand-button {
    top: 10px;
    right: 10px;
  }

  :deep(.panel-section-summary),
  :deep(.panel-subsection-summary),
  :deep(.panel-section-body),
  :deep(.panel-subsection-body) {
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>
